import { useChecklist } from '@tour-kit/checklists';
import { useTours } from '@tour-kit/react';
import { Button, cn } from '@up2stay/up2promo-ui';
import { Check, ChevronRight, ListChecks, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import {
  GET_STARTED_CHECKLIST_ID,
  GUIDANCE_TARGET,
  ORIENTATION_TOUR_ID,
  type GetStartedTaskId,
} from '@/core/guidance/constants';
import { useGuidanceStore } from '@/core/guidance/guidance.store';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellUsesMobileTabBar } from '@/core/utils/shell-chrome';

type GetStartedChecklistUiProps = {
  onSkip: (taskId: GetStartedTaskId) => void | Promise<void>;
  onSharePersisted: () => void | Promise<void>;
};

export function GetStartedChecklistUi({
  onSkip,
  onSharePersisted,
}: GetStartedChecklistUiProps) {
  const dict = useDictionary();
  const copy = dict.guidance.checklist;
  const titleId = useId();
  const {
    checklist,
    visibleTasks,
    progress,
    isDismissed,
    isComplete,
    executeAction,
    dismiss,
    restore,
  } = useChecklist(GET_STARTED_CHECKLIST_ID);

  const { isTourCompleted, isTourSkipped } = useTours();
  const isTourActive = useGuidanceStore((s) => s.isTourActive);
  const setChecklistPanelOpen = useGuidanceStore(
    (s) => s.setChecklistPanelOpen,
  );
  const setChecklistLauncherVisible = useGuidanceStore(
    (s) => s.setChecklistLauncherVisible,
  );
  const restoreNonce = useGuidanceStore((s) => s.checklistRestoreNonce);
  const shareSignal = useGuidanceStore((s) => s.getStartedShareSignal);
  const lastRestore = useRef(0);
  const lastShare = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [forceShow, setForceShow] = useState(false);
  const usesMobileTabBar = shellUsesMobileTabBar();

  const [welcomeFallback, setWelcomeFallback] = useState(false);

  const welcomeDone =
    isTourCompleted(ORIENTATION_TOUR_ID) ||
    isTourSkipped(ORIENTATION_TOUR_ID) ||
    isTourActive ||
    welcomeFallback;

  // If orientation never becomes active (storage race / blocked UI), still
  // surface the checklist — skip/dismiss stay available.
  useEffect(() => {
    if (welcomeDone) return;
    const timer = window.setTimeout(() => {
      setWelcomeFallback(true);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [welcomeDone]);

  useEffect(() => {
    if (restoreNonce === 0 || restoreNonce === lastRestore.current) return;
    lastRestore.current = restoreNonce;
    restore();
    setForceShow(true);
    setOpen(true);
  }, [restoreNonce, restore]);

  useEffect(() => {
    if (shareSignal === 0 || shareSignal === lastShare.current) return;
    lastShare.current = shareSignal;
    void onSharePersisted();
  }, [shareSignal, onSharePersisted]);

  useEffect(() => {
    setChecklistPanelOpen(open);
    return () => setChecklistPanelOpen(false);
  }, [open, setChecklistPanelOpen]);

  const launcherVisible =
    !!checklist &&
    welcomeDone &&
    (forceShow ||
      !(isDismissed || (isComplete && checklist.config.hideOnComplete)));
  useEffect(() => {
    setChecklistLauncherVisible(launcherVisible);
    return () => setChecklistLauncherVisible(false);
  }, [launcherVisible, setChecklistLauncherVisible]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        launcherRef.current?.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled])',
    );
    firstFocusable?.focus();
  }, [open]);

  if (!checklist || !welcomeDone) {
    return <GuidanceChecklistLauncherPlaceholder />;
  }

  if (
    !forceShow &&
    (isDismissed || (isComplete && checklist.config.hideOnComplete))
  ) {
    return <GuidanceChecklistLauncherPlaceholder />;
  }

  const incompleteTasks = visibleTasks.filter((task) => !task.completed);
  const completedTasks = visibleTasks.filter((task) => task.completed);

  return (
    <div
      className={cn(
        'get-started-checklist fixed z-40 flex flex-col items-end gap-3',
        'right-[max(1rem,env(safe-area-inset-right))]',
        usesMobileTabBar
          ? 'bottom-[calc(var(--shell-mobile-tab-bar-clearance)+0.5rem)]'
          : 'bottom-[max(1.25rem,env(safe-area-inset-bottom))]',
      )}
    >
      {open ? (
        <div
          ref={panelRef}
          className={cn(
            'get-started-checklist__panel w-[min(22rem,calc(100vw-2rem))]',
            'overflow-hidden rounded-[var(--radius-surface,1rem)]',
            'border border-border/80 bg-card/95 text-card-foreground shadow-md',
            'backdrop-blur-md',
            'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-200',
          )}
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
        >
          <div className="flex items-start justify-between gap-3 px-4 pt-4">
            <div className="min-w-0 flex-1 space-y-2">
              <ProgressMeter
                completed={progress.completed}
                total={progress.total}
              />
              <h2
                id={titleId}
                className="text-balance text-base font-semibold tracking-tight"
              >
                {copy.title}
              </h2>
              <p className="text-xs text-muted-foreground">
                {copy.progress
                  .replace('{completed}', String(progress.completed))
                  .replace('{total}', String(progress.total))}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-muted-foreground"
              onClick={() => {
                setOpen(false);
                launcherRef.current?.focus();
              }}
              aria-label={copy.closePanel}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>

          <div className="px-2 pb-2 pt-3">
            {progress.remaining === 0 ? (
              <div className="rounded-[var(--radius-nested,0.75rem)] bg-secondary/80 px-3 py-4 text-center">
                <p className="text-sm font-medium text-foreground">
                  {copy.allComplete}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {copy.allCompleteHint}
                </p>
              </div>
            ) : (
              <ul className="max-h-[min(22rem,46vh)] space-y-0.5 overflow-y-auto overscroll-contain">
                {incompleteTasks.map((task) => {
                  const taskId = task.config.id as GetStartedTaskId;
                  return (
                    <li key={taskId}>
                      <div
                        className={cn(
                          'group flex items-stretch gap-1 rounded-[var(--radius-nested,0.75rem)]',
                          'transition-colors duration-150 ease-out',
                          'hover:bg-accent/70 motion-reduce:transition-none',
                        )}
                      >
                        <button
                          type="button"
                          className={cn(
                            'flex min-h-11 min-w-0 flex-1 items-center gap-3 px-2.5 py-2 text-left',
                            'rounded-[var(--radius-nested,0.75rem)]',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                          )}
                          onClick={() => {
                            executeAction(taskId);
                            setOpen(false);
                          }}
                        >
                          <span
                            className={cn(
                              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                              'border border-border bg-background',
                            )}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-foreground">
                              {String(task.config.title)}
                            </span>
                            {task.config.description ? (
                              <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                                {String(task.config.description)}
                              </span>
                            ) : null}
                          </span>
                          <ChevronRight
                            className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 motion-reduce:opacity-70"
                            aria-hidden
                          />
                        </button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="my-1 mr-1 h-9 shrink-0 px-2.5 text-xs text-muted-foreground"
                          onClick={() => {
                            void onSkip(taskId);
                          }}
                        >
                          {copy.skip}
                        </Button>
                      </div>
                    </li>
                  );
                })}
                {completedTasks.length > 0 ? (
                  <li className="px-2.5 pb-1 pt-2">
                    <p className="text-[0.6875rem] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                      {copy.doneSection}
                    </p>
                  </li>
                ) : null}
                {completedTasks.map((task) => {
                  const taskId = task.config.id as GetStartedTaskId;
                  return (
                    <li key={taskId}>
                      <div className="flex min-h-10 items-center gap-3 px-2.5 py-1.5 opacity-60">
                        <span
                          className={cn(
                            'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                            'bg-foreground text-background',
                          )}
                          aria-hidden
                        >
                          <Check className="h-3 w-3" strokeWidth={2.5} />
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-foreground line-through decoration-muted-foreground/70">
                          {String(task.config.title)}
                        </span>
                        <span className="sr-only">{copy.taskDone}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border/70 px-3 py-2.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => {
                dismiss();
                setOpen(false);
                setForceShow(false);
              }}
            >
              {copy.dismiss}
            </Button>
            {progress.remaining > 0 ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  const next = incompleteTasks[0];
                  if (!next) return;
                  executeAction(next.config.id);
                  setOpen(false);
                }}
              >
                {copy.continue}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  setOpen(false);
                  launcherRef.current?.focus();
                }}
              >
                {copy.done}
              </Button>
            )}
          </div>
        </div>
      ) : null}

      <button
        ref={launcherRef}
        type="button"
        data-guidance-target={GUIDANCE_TARGET.checklistLauncher}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'get-started-checklist__launcher',
          'flex min-h-11 items-center gap-2.5 rounded-full px-3.5 py-2',
          'border border-border/80 bg-card/95 text-sm font-medium text-foreground shadow-md',
          'backdrop-blur-md',
          'transition-[background-color,transform,box-shadow] duration-200 ease-out',
          'hover:bg-accent active:scale-[0.98]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'motion-reduce:transition-none motion-reduce:active:scale-100',
        )}
        aria-label={copy.launcherAria}
        aria-expanded={open}
        aria-controls={open ? titleId : undefined}
      >
        <span
          className="relative flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-background"
          aria-hidden
        >
          <ListChecks className="h-3.5 w-3.5" strokeWidth={2.25} />
          {progress.remaining > 0 ? (
            <span
              className={cn(
                'absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center',
                'rounded-full bg-foreground px-1 text-[10px] font-semibold text-background',
                'ring-2 ring-card',
              )}
            >
              {progress.remaining > 9 ? '9+' : progress.remaining}
            </span>
          ) : null}
        </span>
        <span>{copy.launcherLabel}</span>
      </button>
    </div>
  );
}

function ProgressMeter({
  completed,
  total,
}: {
  completed: number;
  total: number;
}) {
  const count = Math.max(total, 1);
  return (
    <div className="flex max-w-[10rem] items-center gap-1" aria-hidden>
      {Array.from({ length: count }, (_, index) => {
        const filled = index < completed;
        return (
          <span
            key={index}
            className={cn(
              'h-1 flex-1 rounded-full transition-[background-color,transform] duration-200 ease-out',
              'motion-reduce:transition-none',
              filled ? 'scale-y-100 bg-foreground' : 'scale-y-90 bg-border',
            )}
          />
        );
      })}
    </div>
  );
}

export function GuidanceChecklistLauncherPlaceholder() {
  const dict = useDictionary();
  const usesMobileTabBar = shellUsesMobileTabBar();

  return (
    <button
      type="button"
      data-guidance-target={GUIDANCE_TARGET.checklistLauncher}
      className={cn(
        'pointer-events-none fixed z-40 flex items-center gap-2 rounded-full opacity-0',
        'border border-border/80 bg-card/95 px-3.5 py-2',
        'text-sm font-medium text-foreground shadow-md',
        'right-[max(1rem,env(safe-area-inset-right))]',
        usesMobileTabBar
          ? 'bottom-[calc(var(--shell-mobile-tab-bar-clearance)+0.5rem)]'
          : 'bottom-[max(1.25rem,env(safe-area-inset-bottom))]',
      )}
      aria-hidden
      tabIndex={-1}
    >
      <span>{dict.guidance.checklist.launcherLabel}</span>
    </button>
  );
}
